<!-- src/components/ProductCardNew.svelte -->
<script>
	import { truncateString, percentageOff, daysRemaining } from '$lib/helpers';
	import { onMount } from 'svelte';
	import { format, parseISO, isValid } from 'date-fns';
	import { browser } from '$app/environment';
	import Image from './Image.svelte';
	import TicketToggle from './TicketToggle.svelte';
	import ProgressBar from './ProgressBar.svelte';
	import { ToastContainer, BootstrapToast } from 'svelte-toasts';
	import { FacebookPixel, fb } from '@beyonk/svelte-facebook-pixel';
	import posthog from 'posthog-js'; // Ensure PostHog is initialized in your app entry

	import { favesStore } from '$stores/faves';
	import { theCart } from '$stores/cart';
	import { prizes } from '$stores/prizes';
	import { ticketsStore, LIMITED_PIDS, MAX_LIMIT } from '$stores/tickets';
	import HeartOutline from '$components/HeartOutline.svelte';
	import Heart from '$components/Heart.svelte';
	import { logError } from '$lib/helpers/errors';
	import { isAppUser } from '$stores/isAppUser';

	export let data;
	export let isSingle = false;
	export let isListing = false;
	export let isEnded = false;
	export let isHighlight = false;
	export let isUpsell = false;

	let heart = false;
	let startDate;
	let endDate;
	let qty = 1;
	let currentImageIndex = 0;
	let imageRotationInterval;

	let updatedData = data;
	let liveStockValue = data.stock || 0;

	// PostHog feature flag check
	// If this flag is off, we won't update from the live `$prizes` store.
	$: reactivityEnabled = browser && posthog.getFeatureFlag('reactive-prize-updates') === 'test';

	// Compute some reactive values
	$: subPrize = data.category === 'subscriber';
	$: individualCost = Number(updatedData?.price ?? 0);
	$: cost = (individualCost * qty)?.toFixed(2);
	$: bundleDiscount = $theCart?.ticketBundles?.bundleDiscountAmount || 0;
	$: bundleCostOff = percentageOff(cost, bundleDiscount);
	$: daysLeft = daysRemaining(updatedData.endDate);

	// Purchased tickets and remaining limit (user-specific)
	$: purchasedTickets = ticketsStore.getCount(updatedData.id) || 0;
	$: remainingTickets = LIMITED_PIDS.includes(updatedData.id)
		? MAX_LIMIT - purchasedTickets
		: Infinity;

	// Conditionally update updatedData and liveStockValue based on $prizes and the feature flag
	$: {
		if (reactivityEnabled) {
			const prizeFromStore = $prizes.find((p) => p.id === data.id);
			if (prizeFromStore) {
				liveStockValue = prizeFromStore.stock;
				updatedData = {
					...data,
					...prizeFromStore,
					initStock: prizeFromStore.initStock || data.initStock || 100
				};
			} else {
				liveStockValue = data.stock || 0;
				updatedData = {
					...data,
					initStock: data.initStock || data.stock || 100
				};
			}
		} else {
			// Reactivity disabled: just stick with the initial data
			liveStockValue = data.stock || 0;
			updatedData = {
				...data,
				initStock: data.initStock || data.stock || 100
			};
		}
	}

	// Combined images for carousel
	$: combinedImages = [
		...(updatedData?.featured_image
			? [{ url: updatedData.featured_image, path: 'featured_image' }]
			: []),

		...(Array.isArray(updatedData?.images)
			? updatedData.images.map((img) =>
					typeof img === 'string'
						? { url: img, path: 'extra' }
						: { url: img.url, path: img.path ?? 'extra' }
				)
			: [])
	];

	// Card classes
	$: cardClasses = `card ${isListing ? 'listing-card' : ''} ${isHighlight ? 'highlight-card' : ''}`;

	let touchStartX = 0;
	let touchEndX = 0;

	function handleTouchStart(event) {
		touchStartX = event.touches[0].clientX;
	}

	function handleTouchMove(event) {
		touchEndX = event.touches[0].clientX;
	}

	function handleTouchEnd() {
		if (touchEndX < touchStartX) {
			nextImage();
		} else if (touchEndX > touchStartX) {
			prevImage();
		}
	}

	function nextImage() {
		currentImageIndex = (currentImageIndex + 1) % combinedImages.length;
	}

	function prevImage() {
		currentImageIndex = (currentImageIndex - 1 + combinedImages.length) % combinedImages.length;
	}

	onMount(() => {
		if (!browser) return;

		if (updatedData.startDate) {
			startDate = format(new Date(updatedData.createdAt), 'dd/MM/yyyy');
		}
		if (updatedData.endDate) {
			const parsed =
				typeof updatedData.endDate === 'string'
					? parseISO(updatedData.endDate)
					: new Date(updatedData.endDate);
			endDate = isValid(parsed) ? format(parsed, 'dd/MM/yyyy') : null;
		} else {
			endDate = null;
		}

		const unsubscribeFaves = favesStore.subscribe(($favesStore) => {
			heart = $favesStore.includes(updatedData.id);
		});

		if (combinedImages.length > 0) {
			imageRotationInterval = setInterval(() => {
				nextImage();
			}, 2000);
		}

		const unsubscribeTickets = ticketsStore.subscribe(() => {
			// Trigger reactivity if enabled (no need for logic here; just ensures stores update)
			// console.log('Initialized tickets:', $ticketsStore);
		});

		return () => {
			if (imageRotationInterval) clearInterval(imageRotationInterval);
			unsubscribeFaves();
			unsubscribeTickets();
		};
	});

	function toggleFave() {
		try {
			if (heart) {
				favesStore.update((items) => items.filter((item) => item !== updatedData.id));
				heart = false;
			} else {
				favesStore.update((items) => [...items, updatedData.id]);
				heart = true;
				posthog.capture('wishlist_added', {
					currency: 'GBP',
					value: cost,
					item_id: updatedData?.name?.replace(' ', '_'),
					item_name: updatedData.name
				});
				fb.track('AddToWishlist', {
					content_name: updatedData?.name,
					content_category: updatedData?.category,
					content_ids: updatedData?.id,
					contents: updatedData,
					currency: 'GBP',
					value: cost
				});
			}
		} catch (error) {
			console.error('Failed to toggle fave:', error);
			logError(error, 'Failed to toggle fave');
		}
	}

	function increaseQty() {
		if (qty < remainingTickets && qty < liveStockValue) {
			ticketsStore.addCount(updatedData.id, 1);
			qty += 1;
		} else {
			console.warn(`Cannot purchase more than allowed tickets for ${updatedData.id}`);
		}
	}

	function decreaseQty() {
		if (qty > 1) {
			ticketsStore.setCount(updatedData.id, purchasedTickets - 1);
			qty -= 1;
		}
	}
</script>

<FacebookPixel />

<div class={cardClasses}>
	<article class={isSingle ? 'product-card single-card' : 'product-card'}>
		{#if subPrize}
			<p class="sub-only">Subscriber Exclusive</p>
		{/if}

		<div
			class="image"
			on:touchstart|passive={handleTouchStart}
			on:touchmove|passive={handleTouchMove}
			on:touchend={handleTouchEnd}
		>
			<div class="ind-ticket-cost">
				<p>£{individualCost?.toFixed(2)} <sup style="margin-top: 8px;">each</sup></p>
			</div>

			{#if combinedImages.length > 0}
				<Image threshold={0} src={combinedImages[currentImageIndex]?.url} alt={updatedData?.name} />
			{/if}

			{#if !isSingle && !isUpsell}
				<a href="/prizes/{updatedData.id}" target="_self" class="prize-link" aria-label="View Prize"
				></a>
			{/if}
		</div>

		<div class="product-card--wrap">
			<div class="title-wrap">
				{#if !isSingle}
					<h2>{truncateString(updatedData?.name, 60)}</h2>
				{/if}

				{#if !isUpsell && liveStockValue > 0 && updatedData.initStock && !subPrize}
					<ProgressBar currentStock={liveStockValue} initialStock={updatedData.initStock} />
				{/if}
			</div>

			{#if !isEnded}
				<div class="flex end-total {isUpsell ? 'upsell' : ''}">
					<div>
						<p class="days-left">
							{#if daysLeft === 0}
								<strong>9PM Tonight</strong>
							{:else if daysLeft === 1}
								<strong>Tomorrow</strong>
							{:else}
								<strong>{daysLeft} Days</strong>
							{/if}
							<img src="/images/fast-timeOpt.png" alt="Fast Timer" />
						</p>
						{#if !isHighlight && !isUpsell && updatedData?.endDate}
							{#if isValid(typeof updatedData.endDate === 'string' ? parseISO(updatedData.endDate) : new Date(updatedData.endDate))}
								<p class="small-p">
									Ends {format(
										typeof updatedData.endDate === 'string'
											? parseISO(updatedData.endDate)
											: new Date(updatedData.endDate),
										'dd/MM/yyyy'
									)}
								</p>
							{/if}
						{/if}
					</div>

					<div class="flex-end">
						{#if bundleDiscount > 0}
							<p class="total">
								£<span class="change-ani">{(Number(cost) - bundleCostOff)?.toFixed(2)}</span>
							</p>
							<p>
								<strike>£<span class="change-ani">{cost}</span></strike>
								<span class="change-ani red-banner">{bundleDiscount}% Off</span>
							</p>
						{:else if subPrize}
							<p class="total">FREE!</p>
						{:else}
							<p class="total">£<span class="change-ani">{cost}</span></p>
						{/if}
					</div>
				</div>
			{/if}

			<div class="content">
				{#if liveStockValue === 0}
					<p class="winner">Winner <span>{updatedData.winnerName}</span></p>
					{#if endDate}
						<p class="end-date">Ended: {endDate}</p>
					{/if}
				{:else}
					<TicketToggle
						{isHighlight}
						{isUpsell}
						data={updatedData}
						pid={updatedData.id}
						{isSingle}
						{isListing}
						bind:qty
						on:increase={increaseQty}
						on:decrease={decreaseQty}
					/>

					{#if isSingle && !subPrize}
						<div class="center">
							<a class="btn-two postal-btn" target="_self" href="/terms#free">
								For free postal entry method
								<img src="/images/openOpt.png" alt="Letter" />
							</a>
						</div>
					{/if}
				{/if}
			</div>
		</div>

		<ToastContainer let:data>
			<BootstrapToast {data} />
		</ToastContainer>
	</article>
</div>

<style lang="scss">
	.red-banner {
		background-color: #fe1100;
		color: #fff;
		padding: 3px 6px;
		white-space: nowrap;
		font-weight: 700;
	}

	.small-p {
		font-size: 0.86em !important;
	}

	.btn-two {
		display: inline-block;
		margin-top: 20px;
	}

	.flex-end {
		display: flex;
		flex-direction: column;
		text-align: right;
	}

	article {
		position: relative;
		box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
		transition: 0.3s;
		background-color: #fff;
		border-radius: 10px;
		min-width: 300px;
	}

	.sub-only {
		position: absolute;
		top: 0;
		left: 0;
		background-color: #fe1100;
		color: #fff;
		padding: 4px 8px;
		z-index: 10;
		border-radius: 0 0 10px 0;
		font-weight: 700;
		font-size: 0.8em;
	}

	.ind-ticket-cost {
		position: absolute;
		bottom: 0;
		right: 0;
		z-index: 10;
		background-color: #fff;
		width: 70px;
		height: 50px;
		display: flex;
		align-items: center;
		justify-content: center;
		border-radius: 10px 0 0 0;
		padding-bottom: 10px;

		p {
			color: #fe1100;
			font-weight: bold;
			margin: 0;
			font-size: 1.2rem;
		}

		sup {
			display: block;
			margin-top: 4px;
		}
	}

	.highlight-card .total,
	.highlight-card .ind-ticket-cost p {
		color: goldenrod;
	}

	.product-card--wrap {
		padding: 6px;
	}

	.total {
		font-weight: 700;
		font-size: 22px;
		line-height: 25px;
		color: #fe1100;
		padding-left: 10px;

		@media (max-width: 469px) {
			padding-left: 0;
			margin-top: 12px;
		}
	}

	.card {
		padding: 10px;

		h2 {
			font-size: 1em;
			text-align: left;
			font-weight: 700;
			margin: 5px 0;
			min-height: 50px;
		}

		@media (min-width: 749px) {
			display: flex;
			align-items: stretch;
			max-width: 420px;
		}
	}

	.image {
		position: relative;
		overflow: hidden;
		text-align: center;
		min-height: 262px;
		min-width: 240px;
		border-radius: 6px 6px 0 0;
	}

	.end-total {
		display: flex;
		justify-content: space-between;
		margin-bottom: 12px;
		align-items: flex-start;

		&.upsell > div {
			min-height: 78px;
		}

		@media (max-width: 469px) {
			flex-direction: column;

			> div {
				width: 100%;
				justify-content: center;
				text-align: center;
			}

			.days-left {
				display: flex;
				justify-content: center;
				margin-bottom: 10px;

				img {
					margin-left: 10px;
				}
			}
		}
	}

	.winner {
		font-weight: 700;
		font-size: 1.2em;

		span {
			color: #fe1100;
		}
	}

	.end-date {
		font-size: 0.9em;
		font-weight: 600;
	}

	p {
		font-size: 0.95em;
		margin: 0;
	}

	.content {
		max-width: 420px;
		margin: auto;
	}

	strike {
		font-size: 0.8em;
		display: block;
	}

	.card.listing-card {
		padding: 5px;

		article {
			min-width: auto;

			.image {
				min-width: 120px;
				min-height: 120px;

				img {
					max-width: 100%;
				}
			}

			h2 {
				font-size: 0.85em;
			}

			p {
				font-size: 14px;
			}

			p.total {
				font-size: 16px;
			}
		}
	}

	.postal-btn {
		font-weight: 700;
		font-size: 18px;

		img {
			margin: 0px 0 -6px 8px;
			max-width: 30px;
			height: auto;
		}
	}

	.days-left {
		display: flex;
		align-items: center;
		white-space: nowrap;

		img {
			max-width: 30px;
			height: auto;
			margin-left: 4px;
		}
	}

	.prize-link {
		position: absolute;
		top: 0;
		left: 0;
		width: 100%;
		height: 100%;
		z-index: 1;
	}
</style>
